"use client";

import React from "react";
import { formatDistanceToNow } from "date-fns";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import type { TabContentProps } from "@/components/shared/UniversalDetailPage/types/detailPage";
import type { Ticket } from "@/lib/types/database";

/**
 * Ticket Activity Timeline Tab Component
 */
export const TicketActivityTimelineTab = React.memo(({ item }: TabContentProps<Ticket>) => {
  return (
    <Card>
      <CardHeader>
        <CardTitle className="text-lg">Ticket Activity</CardTitle>
      </CardHeader>
      <CardContent>
        <div className="space-y-3">
          {/* Ticket Created */}
          <div className="flex items-start gap-3 p-3 bg-card-50 rounded-lg">
            <div className="w-2 h-2 bg-blue-500 rounded-full mt-2"></div>
            <div>
              <p className="text-sm font-medium text-card-foreground">Ticket Created</p>
              <p className="text-xs text-gray-500">
                {item.created_at
                  ? formatDistanceToNow(new Date(item.created_at), { addSuffix: true })
                  : "Unknown"}
              </p>
            </div>
          </div>

          {/* Ticket Updated */}
          {item.updated_at && item.created_at && item.updated_at !== item.created_at && (
            <div className="flex items-start gap-3 p-3 bg-card-50 rounded-lg">
              <div className="w-2 h-2 bg-yellow-500 rounded-full mt-2"></div>
              <div>
                <p className="text-sm font-medium text-card-foreground">Ticket Updated</p>
                <p className="text-xs text-gray-500">
                  {item.updated_at
                    ? formatDistanceToNow(new Date(item.updated_at), { addSuffix: true })
                    : "Unknown"}
                </p>
              </div>
            </div>
          )}

          {/* Ticket Resolved */}
          {item.resolved_at && (
            <div className="flex items-start gap-3 p-3 bg-green-50 rounded-lg dark:bg-green-950/40">
              <div className="w-2 h-2 bg-green-500 rounded-full mt-2"></div>
              <div>
                <p className="text-sm font-medium text-card-foreground">Ticket Resolved</p>
                <p className="text-xs text-gray-500">
                  {formatDistanceToNow(new Date(item.resolved_at), { addSuffix: true })}
                </p>
              </div>
            </div>
          )}

          {/* Ticket Closed */}
          {item.closed_at && (
            <div className="flex items-start gap-3 p-3 bg-card-50 rounded-lg">
              <div className="w-2 h-2 bg-card-500 rounded-full mt-2"></div>
              <div>
                <p className="text-sm font-medium text-card-foreground">Ticket Closed</p>
                <p className="text-xs text-gray-500">
                  {formatDistanceToNow(new Date(item.closed_at), { addSuffix: true })}
                </p>
              </div>
            </div>
          )}
        </div>
      </CardContent>
    </Card>
  );
});

TicketActivityTimelineTab.displayName = "TicketActivityTimelineTab";
